import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { getLastBackupAt, getMeta, setMeta } from '~/storage/repositories/metaRepository'
import { META_KEY } from '~/storage/schema'
import { ok, type Result } from '~/utils/result'

/**
 * When to remind the user to back up (D-084, closing the reminder half of L-007).
 *
 * Two triggers, and the second exists because the first is not enough:
 *
 *  1. **After a weekly review is finalized.** The moment the user has just
 *     entered the most data and is least likely to resent being asked.
 *  2. **After fourteen days with no backup**, anywhere in the app. The review
 *     prompt alone would never fire for someone who has stopped reviewing —
 *     which is exactly the user most likely to lose data.
 *
 * **Never blocking, never punitive** (D-022 and the quality bar). No count of
 * missed backups, no streak, no badge. A dismissal is honoured for seven days
 * rather than forever: a reminder that can be silenced permanently in one tap
 * will be, and the protection it offers is real.
 */

/** Days without a backup before the standing notice appears. */
export const REMINDER_AFTER_DAYS = 14

/** How long a dismissal is honoured. */
export const DISMISSAL_HOLDS_DAYS = 7

export type BackupReminder =
  | { readonly kind: 'none' }
  /** No backup has ever been made on this install. */
  | { readonly kind: 'never' }
  | { readonly kind: 'stale'; readonly lastBackupAt: string; readonly daysAgo: number }

export interface ReminderContext {
  /** True immediately after a weekly review is finalized. */
  readonly justFinishedReview: boolean
}

export async function getBackupReminder(
  db: SolaDatabase,
  now: Date,
  context: ReminderContext = { justFinishedReview: false },
): Promise<Result<BackupReminder, StorageError>> {
  const dismissed = await getMeta<string>(db, META_KEY.backupReminderDismissedAt)
  if (!dismissed.ok) return dismissed

  if (dismissed.value !== undefined && withinDays(dismissed.value, now, DISMISSAL_HOLDS_DAYS)) {
    return ok({ kind: 'none' })
  }

  const lastBackup = await getLastBackupAt(db)
  if (!lastBackup.ok) return lastBackup

  if (lastBackup.value === undefined) return ok({ kind: 'never' })

  const daysAgo = wholeDaysBetween(lastBackup.value, now)

  /* Finishing a review shows the reminder at any age, because that is the
     moment it costs least. Outside that moment it waits for the fourteen-day
     mark, so the app is not asking on a day the user just backed up. */
  if (context.justFinishedReview || daysAgo >= REMINDER_AFTER_DAYS) {
    return ok({ kind: 'stale', lastBackupAt: lastBackup.value, daysAgo })
  }

  return ok({ kind: 'none' })
}

export async function dismissBackupReminder(
  db: SolaDatabase,
  now: Date,
): Promise<Result<void, StorageError>> {
  return setMeta(db, META_KEY.backupReminderDismissedAt, now.toISOString())
}

/**
 * Cleared whenever a backup succeeds.
 *
 * Otherwise a dismissal made before a backup would keep suppressing the notice
 * for days after it had become relevant again — the reminder would be quiet for
 * the wrong reason, which is the failure mode a reminder cannot afford.
 */
export async function clearBackupReminderDismissal(
  db: SolaDatabase,
): Promise<Result<void, StorageError>> {
  return setMeta(db, META_KEY.backupReminderDismissedAt, undefined)
}

function withinDays(isoTimestamp: string, now: Date, days: number): boolean {
  return wholeDaysBetween(isoTimestamp, now) < days
}

function wholeDaysBetween(isoTimestamp: string, now: Date): number {
  const then = new Date(isoTimestamp).getTime()
  if (Number.isNaN(then)) return Number.POSITIVE_INFINITY
  return Math.floor((now.getTime() - then) / (24 * 60 * 60 * 1000))
}
